import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface ImageLightboxProps {
  urls: string[];
  initialIndex: number;
  onClose: () => void;
}

/** Fullscreen image viewer — arrow/swipe-free prev·next buttons, Escape to close. */
export function ImageLightbox({ urls, initialIndex, onClose }: ImageLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const count = urls.length;

  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);
  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  // Portal to <body>: ancestor transforms (e.g. animate-rise) would otherwise
  // turn position:fixed into a local containing block and trap the overlay.
  return createPortal(
    <dialog
      open
      aria-label="이미지 크게 보기"
      className="fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-center justify-center bg-black/85 p-4"
    >
      {/* Mouse-only backdrop close; keyboard users close via Escape or the X button. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <img
        src={urls[index]}
        alt=""
        className="relative max-h-[85vh] max-w-full rounded-xl object-contain"
      />
      <button
        type="button"
        aria-label="닫기"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
      >
        <X className="h-5 w-5" />
      </button>
      {count > 1 ? (
        <>
          <button
            type="button"
            aria-label="이전 이미지"
            onClick={prev}
            className="absolute left-3 z-10 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            aria-label="다음 이미지"
            onClick={next}
            className="absolute right-3 z-10 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <span className="absolute bottom-5 rounded-full bg-white/15 px-3 py-1 text-xs text-white">
            {index + 1} / {count}
          </span>
        </>
      ) : null}
    </dialog>,
    document.body
  );
}
